import type { ReactNode } from "react";
import { Link } from "react-router";

export interface ResolvedReference {
  target: string;
  entryId: string;
}

/**
 * The Markdown of a section as React elements, never as HTML: paragraphs, headings, lists, quotes, emphasis, code, web links and
 * references [[Titel]] or [[Titel|Text]]. A reference becomes a link only when the server resolved it for this view; any other
 * stays plain text without a hint (F5). The subset covers what notes at the table need, without a further library.
 */
export function Markdown(props: { text: string; references: ResolvedReference[]; entryPath: (entryId: string) => string }) {
  const blocks = parseBlocks(props.text);
  const inline = (text: string) => renderInline(text, props.references, props.entryPath);
  return (
    <div className="markdown">
      {blocks.map((block, i) => {
        switch (block.kind) {
          case "heading": {
            const Tag = (["h3", "h4", "h5"] as const)[Math.min(block.level, 3) - 1]!;
            return <Tag key={i}>{inline(block.text)}</Tag>;
          }
          case "list": {
            const Tag = block.ordered ? "ol" : "ul";
            return (
              <Tag key={i}>
                {block.items.map((item, j) => (
                  <li key={j}>{inline(item)}</li>
                ))}
              </Tag>
            );
          }
          case "quote":
            return <blockquote key={i}>{withBreaks(block.text, inline)}</blockquote>;
          case "code":
            return (
              <pre key={i}>
                <code>{block.text}</code>
              </pre>
            );
          case "rule":
            return <hr key={i} />;
          default:
            return <p key={i}>{withBreaks(block.text, inline)}</p>;
        }
      })}
    </div>
  );
}

type Block =
  | { kind: "paragraph"; text: string }
  | { kind: "heading"; level: number; text: string }
  | { kind: "list"; ordered: boolean; items: string[] }
  | { kind: "quote"; text: string }
  | { kind: "code"; text: string }
  | { kind: "rule" };

const bullet = /^\s*[-*+]\s+(.*)$/;
const numbered = /^\s*\d+[.)]\s+(.*)$/;

function parseBlocks(text: string): Block[] {
  const lines = text.replace(/\r\n?/g, "\n").split("\n");
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (line.trim() === "") {
      i++;
      continue;
    }
    if (line.trimStart().startsWith("```")) {
      const code: string[] = [];
      i++;
      while (i < lines.length && !lines[i]!.trimStart().startsWith("```")) code.push(lines[i++]!);
      i++;
      blocks.push({ kind: "code", text: code.join("\n") });
      continue;
    }
    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      blocks.push({ kind: "heading", level: heading[1]!.length, text: heading[2]!.replace(/\s+#+\s*$/, "") });
      i++;
      continue;
    }
    if (/^\s*([-*_])(\s*\1){2,}\s*$/.test(line)) {
      blocks.push({ kind: "rule" });
      i++;
      continue;
    }
    if (bullet.test(line) || numbered.test(line)) {
      const ordered = !bullet.test(line);
      const pattern = ordered ? numbered : bullet;
      const items: string[] = [];
      while (i < lines.length && pattern.test(lines[i]!)) {
        items.push(pattern.exec(lines[i]!)![1]!);
        i++;
        // Indented lines continue the item.
        while (i < lines.length && /^\s{2,}\S/.test(lines[i]!) && !bullet.test(lines[i]!) && !numbered.test(lines[i]!)) {
          items[items.length - 1] += ` ${lines[i]!.trim()}`;
          i++;
        }
      }
      blocks.push({ kind: "list", ordered, items });
      continue;
    }
    if (line.trimStart().startsWith(">")) {
      const quote: string[] = [];
      while (i < lines.length && lines[i]!.trimStart().startsWith(">")) quote.push(lines[i++]!.trimStart().replace(/^>\s?/, ""));
      blocks.push({ kind: "quote", text: quote.join("\n") });
      continue;
    }
    const paragraph: string[] = [];
    while (
      i < lines.length &&
      lines[i]!.trim() !== "" &&
      !/^(#{1,6})\s/.test(lines[i]!) &&
      !lines[i]!.trimStart().startsWith("```") &&
      !lines[i]!.trimStart().startsWith(">") &&
      !bullet.test(lines[i]!) &&
      !numbered.test(lines[i]!)
    ) {
      paragraph.push(lines[i++]!);
    }
    blocks.push({ kind: "paragraph", text: paragraph.join("\n") });
  }
  return blocks;
}

function withBreaks(text: string, inline: (text: string) => ReactNode[]): ReactNode[] {
  return text.split("\n").flatMap((line, i) => (i === 0 ? inline(line) : [<br key={`br${i}`} />, ...inline(line)]));
}

const inlinePattern = /\[\[([^\]\n]+?)\]\]|`([^`]+)`|\*\*(.+?)\*\*|__(.+?)__|\*([^*\s][^*]*?)\*|_([^_\s][^_]*?)_|\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g;

function renderInline(text: string, references: ResolvedReference[], entryPath: (entryId: string) => string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let last = 0;
  let key = 0;
  for (const match of text.matchAll(inlinePattern)) {
    if (match.index > last) nodes.push(text.slice(last, match.index));
    const [whole, reference, code, strong, strong2, em, em2, label, href] = match;
    if (reference !== undefined) {
      const bar = reference.indexOf("|");
      const target = (bar < 0 ? reference : reference.slice(0, bar)).trim();
      const display = (bar < 0 ? "" : reference.slice(bar + 1).trim()) || target;
      const resolved = references.find((r) => r.target.localeCompare(target, "de", { sensitivity: "accent" }) === 0);
      nodes.push(
        resolved ? (
          <Link key={key++} to={entryPath(resolved.entryId)} className="reference">
            {display}
          </Link>
        ) : (
          display
        ),
      );
    } else if (code !== undefined) {
      nodes.push(<code key={key++}>{code}</code>);
    } else if (strong !== undefined || strong2 !== undefined) {
      nodes.push(<strong key={key++}>{renderInline(strong ?? strong2!, references, entryPath)}</strong>);
    } else if (em !== undefined || em2 !== undefined) {
      nodes.push(<em key={key++}>{renderInline(em ?? em2!, references, entryPath)}</em>);
    } else if (label !== undefined && href !== undefined) {
      nodes.push(
        <a key={key++} href={href} target="_blank" rel="noopener noreferrer">
          {label}
        </a>,
      );
    } else {
      nodes.push(whole);
    }
    last = match.index + whole.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}
